import type { ArtifactActionId, ArtifactActionIntent } from "../transport/types";

/**
 * Capability → presentation mapping for per-artifact actions: the ONE
 * place an action id gains user-facing form (its button label + row
 * tooltip). Components render these descriptors verbatim, so "which
 * buttons exist" is pure capability DATA from the transport
 * (`caps.actions`), never host branching — the falsification guard of the
 * "one component, many shells" bet (ui-surface.md "Komponentų dalijimasis
 * tarp projektų"; the §3a purity grep).
 *
 * Pure + DOM-free (compiles under both tsconfigs, unit-tested in
 * test/ui/artifact-actions.test.ts — the download pins double as the
 * browser byte-identity regression). Labels are inline here for now; the
 * shared LT vocab unification (vocab.ts) is a SEPARATE deferred PR.
 */

export interface ArtifactActionDescriptor {
  id: ArtifactActionId;
  /** Button text (table row + versions panel). */
  label: string;
  /** Row-button tooltip — a row action targets the CURRENT version. */
  rowTitle: string;
}

/** Inline LT labels (vocab unification deferred). */
const LABELS: Record<ArtifactActionId, string> = {
  download: "Atsisiųsti",
  open: "Atidaryti",
  import: "Importuoti",
};

/**
 * Resolve advertised action ids to descriptors, PRESERVING the transport's
 * order (first = the primary button). Unknown ids are skipped — capability
 * negotiation: a vendored (older) bundle facing a newer host renders what
 * it understands and never breaks.
 */
export function actionDescriptors(
  actions: readonly ArtifactActionId[],
): ArtifactActionDescriptor[] {
  const out: ArtifactActionDescriptor[] = [];
  for (const id of actions) {
    // Runtime-defensive: a NEWER host may advertise an id this bundle does
    // not know (the union is a compile-time view of THIS version). Use an
    // OWN-property check — a bare `LABELS[id]` would resolve inherited
    // Object.prototype names ("toString", "constructor", …) to functions
    // and wrongly treat them as known labels. hasOwnProperty.call, NOT
    // Object.hasOwn (ES2022): the dialog bundle must run in SU2023 CEF =
    // Chromium 88 / ES2020; ui/tsconfig lib pins this at compile time.
    if (!Object.prototype.hasOwnProperty.call(LABELS, id)) continue;
    const label = LABELS[id];
    out.push({ id, label, rowTitle: `${label} naujausią versiją` });
  }
  return out;
}

/**
 * Build the ONE typed intent an action button emits (single-sourced so the
 * table and versions panel agree, and so the payload shape is pure-tested
 * without a DOM). Omitted `versionNumber` = the artifact's CURRENT version;
 * it is left OFF the object when undefined so callers get a clean payload.
 */
export function artifactActionIntent(
  action: ArtifactActionId,
  artifactId: string,
  versionNumber?: number,
): ArtifactActionIntent {
  return versionNumber === undefined
    ? { action, artifactId }
    : { action, artifactId, versionNumber };
}

/**
 * How the browser component should route one action intent —
 * EXHAUSTIVE dispatch (PR #45 review minor 3): a non-download action a
 * transport cannot perform is "unsupported", NOT a silent no-op. Pure +
 * DOM-free so the routing decision is unit-tested without a DOM harness
 * (test/ui/artifact-actions.test.ts); the component is a thin wrapper that
 * renders each verdict (download → mint+navigate; perform →
 * transport.performArtifactAction; unsupported → visible error status).
 *
 * `canPerform` = the transport exposes performArtifactAction. The switch
 * is exhaustive over {@link ArtifactActionId}: a NEW action id added to the
 * union that is not routed here is a COMPILE error (the `never` guard) —
 * the dispatch can never silently grow a hole.
 */
export type ArtifactActionRoute = "download" | "perform" | "unsupported";

export function planArtifactAction(
  action: ArtifactActionId,
  canPerform: boolean,
): ArtifactActionRoute {
  switch (action) {
    case "download":
      // The browser fulfils "download" itself (getDownloadLink +
      // openExternal) — never via performArtifactAction (byte-identical
      // pre-PR-B behavior).
      return "download";
    case "open":
    case "import":
      return canPerform ? "perform" : "unsupported";
    default: {
      // Unreachable for the known union (actionDescriptors already skips
      // ids this bundle does not know), but forces exhaustiveness at
      // compile time AND degrades to a surfaced "unsupported" at runtime.
      const _exhaustive: never = action;
      void _exhaustive;
      return "unsupported";
    }
  }
}
